import styled from 'styled-components'
import { theme, mediaQuery } from '../../theme'

// Layout Components
export const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 ${theme.spacing.md};
  
  ${mediaQuery.tablet} {
    padding: 0 ${theme.spacing.lg};
  }
`

export const Card = styled.div`
  background: white;
  border-radius: ${theme.borderRadius.lg};
  box-shadow: ${theme.shadows.card};
  padding: ${theme.spacing.lg};
  margin-bottom: ${theme.spacing.lg};
  
  &:hover {
    box-shadow: ${theme.shadows.hover};
    transition: box-shadow 0.2s ease;
  }
`

export const Grid = styled.div`
  display: grid;
  gap: ${theme.spacing.lg};
  grid-template-columns: 1fr;
  
  ${mediaQuery.tablet} {
    grid-template-columns: ${props => props.columns || 'repeat(2, 1fr)'};
  }
  
  ${mediaQuery.desktop} {
    grid-template-columns: ${props => props.desktopColumns || props.columns || 'repeat(3, 1fr)'};
  }
`

// Typography Components
export const Heading1 = styled.h1`
  ${theme.typography.heading.h1}
  font-family: ${theme.typography.heading.fontFamily};
  color: ${theme.colors.primary.main};
  margin-bottom: ${theme.spacing.lg};
`

export const Heading2 = styled.h2`
  ${theme.typography.heading.h2}
  font-family: ${theme.typography.heading.fontFamily};
  color: ${theme.colors.primary.main};
  margin-bottom: ${theme.spacing.md};
`

export const Heading3 = styled.h3`
  ${theme.typography.heading.h3}
  font-family: ${theme.typography.heading.fontFamily};
  color: ${theme.colors.primary.dark};
  margin-bottom: ${theme.spacing.md};
`

export const BodyText = styled.p`
  ${theme.typography.body.p1}
  color: ${theme.colors.secondary.black};
  margin-bottom: ${theme.spacing.md};
`

export const SmallText = styled.p`
  ${theme.typography.body.p2}
  color: ${theme.colors.primary.light};
  margin-bottom: ${theme.spacing.sm};
`

// Button Components
export const Button = styled.button`
  background: ${props => {
    switch(props.variant) {
      case 'secondary': return theme.colors.secondary.green;
      case 'outline': return 'transparent';
      default: return theme.colors.primary.main;
    }
  }};
  color: ${props => props.variant === 'outline' ? theme.colors.primary.main : 'white'};
  border: ${props => props.variant === 'outline' ? `2px solid ${theme.colors.primary.main}` : 'none'};
  padding: ${theme.spacing.md} ${theme.spacing.lg};
  border-radius: ${theme.borderRadius.md};
  font-family: ${theme.typography.body.fontFamily};
  font-weight: 500;
  font-size: 16px;
  cursor: pointer;
  transition: all 0.2s ease;
  width: ${props => props.fullWidth ? '100%' : 'auto'};
  
  &:hover {
    background: ${props => {
      switch(props.variant) {
        case 'secondary': return theme.colors.primary.dark;
        case 'outline': return theme.colors.primary.main;
        default: return theme.colors.primary.dark;
      }
    }};
    color: ${props => props.variant === 'outline' ? 'white' : 'white'};
    transform: translateY(-1px);
    box-shadow: ${theme.shadows.hover};
  }
  
  &:active {
    transform: translateY(0);
    box-shadow: ${theme.shadows.active};
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`

// Input Components
export const Input = styled.input`
  width: 100%;
  padding: ${theme.spacing.md};
  border: 2px solid ${theme.colors.primary.light};
  border-radius: ${theme.borderRadius.md};
  font-family: ${theme.typography.body.fontFamily};
  font-size: 16px;
  
  &:focus {
    outline: none;
    border-color: ${theme.colors.primary.main};
    box-shadow: 0 0 0 3px rgba(64, 92, 115, 0.1);
  }
`

// Status Components
export const StatusBadge = styled.div`
  display: inline-flex;
  align-items: center;
  padding: ${theme.spacing.xs} ${theme.spacing.sm};
  border-radius: ${theme.borderRadius.sm};
  font-size: ${theme.typography.body.p3.fontSize};
  font-weight: 500;
  
  background: ${props => {
    switch(props.status) {
      case 'excellent': return theme.colors.status.success;
      case 'good': return theme.colors.secondary.green;
      case 'fair': return theme.colors.status.warning;
      case 'poor': return theme.colors.status.error;
      default: return theme.colors.status.info;
    }
  }};
  
  color: white;
`

// Loading Components
export const LoadingSpinner = styled.div`
  width: 40px;
  height: 40px;
  border: 4px solid ${theme.colors.primary.light};
  border-top: 4px solid ${theme.colors.primary.main};
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: ${theme.spacing.lg} auto;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`

// Navigation Components
export const NavBar = styled.nav`
  background: ${theme.colors.primary.main};
  padding: ${theme.spacing.md} 0;
  box-shadow: ${theme.shadows.card};
  position: sticky;
  top: 0;
  z-index: 100;
`

export const NavContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 ${theme.spacing.md};
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  ${mediaQuery.tablet} {
    padding: 0 ${theme.spacing.lg};
  }
`

export const NavTitle = styled.h1`
  color: white;
  font-family: ${theme.typography.heading.fontFamily};
  font-size: 24px;
  font-weight: 600;
`

export const NavLinks = styled.div`
  display: flex;
  gap: ${theme.spacing.md};
  
  a {
    color: white;
    text-decoration: none;
    padding: ${theme.spacing.sm} ${theme.spacing.md};
    border-radius: ${theme.borderRadius.sm};
    transition: background 0.2s ease;
    
    &:hover {
      background: ${theme.colors.primary.dark};
    }
    
    &.active {
      background: ${theme.colors.secondary.green};
    }
  }
` 